/**
 * Wishlist Manager - Complete wishlist functionality with localStorage
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'mama_pajama_wishlist';
    this.init();
  }

  init() {
    // Add event listeners to all wishlist buttons
    document.addEventListener('DOMContentLoaded', () => {
      this.attachListeners();
      this.syncButtonStates();
    });

    // Listen for custom wishlist events
    document.addEventListener('wishlist:updated', () => {
      this.syncButtonStates();
    });
  }

  attachListeners() {
    // Use event delegation for dynamically added buttons
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('.insy-wishlist');
      if (wishlistBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleWishlist(wishlistBtn);
      }
      // Handle PDP/Standalone wishlist button
      const pdpWishlistBtn = e.target.closest('.pdp-wishlist-btn');
      if (pdpWishlistBtn) {
        const productId = pdpWishlistBtn.dataset.productId;
        // If it's already in wishlist and it's a link, let it navigate to wishlist page
        if (productId && this.isInWishlist(productId) && pdpWishlistBtn.tagName === 'A') {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        this.toggleWishlist(pdpWishlistBtn);
      }

      // Handle remove from wishlist page
      const removeBtn = e.target.closest('.wishlist-remove-btn');
      if (removeBtn) {
        e.preventDefault();
        const productId = removeBtn.dataset.productId;
        this.removeFromWishlist(productId);
      }
    });
  }

  toggleWishlist(button) {
    const productCard = button.closest('.insy-card');
    
    // Check if it's a standalone button (PDP)
    if (!productCard && !button.classList.contains('pdp-wishlist-btn')) return;

    const productData = productCard ? this.extractProductData(productCard) : this.extractStandaloneProductData(button);

    if (this.isInWishlist(productData.id)) {
      this.removeFromWishlist(productData.id);
      this.showNotification(window.themeVariables.strings.wishlistRemoved, 'remove');
    } else {
      this.addToWishlist(productData);
      this.showNotification(window.themeVariables.strings.wishlistAdded, 'add');
    }

    // Animate button
    this.animateButton(button);
  }

  extractProductData(productCard) {
    const link = productCard.querySelector('.insy-link-cover');
    const title = productCard.querySelector('.insy-info h3 a');
    const price = productCard.querySelector('.insy-info .price');
    const image = productCard.querySelector('.insy-img.is-primary');
    const quickAddBtn = productCard.querySelector('.quick-add-btn');

    return {
      id: quickAddBtn ? quickAddBtn.dataset.variant : Date.now().toString(),
      title: title ? title.textContent.trim() : 'Unknown Product',
      price: price ? price.textContent.trim() : '',
      image: image ? image.src : '',
      handle: link ? link.getAttribute('href') : '#',
      variantId: quickAddBtn ? quickAddBtn.dataset.variant : null,
      timestamp: Date.now()
    };
  }

  extractStandaloneProductData(button) {
    return {
      id: button.dataset.productId,
      title: button.dataset.title,
      price: button.dataset.price,
      image: button.dataset.image,
      handle: button.dataset.handle,
      variantId: button.dataset.variantId,
      timestamp: Date.now()
    };
  }

  getWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading wishlist:', error);
      return [];
    }
  }

  saveWishlist(wishlist) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  addToWishlist(product) {
    const wishlist = this.getWishlist();

    // Check if already exists
    if (!wishlist.find(item => item.id === product.id)) {
      wishlist.push(product);
      this.saveWishlist(wishlist);
    }
  }

  removeFromWishlist(productId) {
    let wishlist = this.getWishlist();
    wishlist = wishlist.filter(item => item.id !== productId);
    this.saveWishlist(wishlist);

    // If we're on the wishlist page, remove the item from DOM
    if (window.location.pathname.includes('wishlist')) {
      const productItem = document.querySelector(`[data-wishlist-id="${productId}"]`);
      if (productItem) {
        productItem.style.animation = 'slideOutRight 0.3s ease forwards';
        setTimeout(() => {
          productItem.remove();
          this.checkEmptyState();
        }, 300);
      }
    }
  }

  isInWishlist(productId) {
    const wishlist = this.getWishlist();
    return wishlist.some(item => item.id === productId);
  }

  syncButtonStates() {
    const wishlistButtons = document.querySelectorAll('.insy-wishlist');
    wishlistButtons.forEach(button => {
      const productCard = button.closest('.insy-card');
      if (productCard) {
        const quickAddBtn = productCard.querySelector('.quick-add-btn');
        const productId = quickAddBtn ? quickAddBtn.dataset.variant : null;

        if (productId && this.isInWishlist(productId)) {
          button.classList.add('is-active');
        } else {
          button.classList.remove('is-active');
        }
      }
    });

    // Sync standalone buttons (PDP)
    const standaloneButtons = document.querySelectorAll('.pdp-wishlist-btn');
    standaloneButtons.forEach(button => {
      const productId = button.dataset.productId;
      if (productId && this.isInWishlist(productId)) {
        button.classList.add('is-active');
        const textSpan = button.querySelector('.wishlist-text');
        if(textSpan) textSpan.textContent = window.themeVariables.strings.viewWishlist;
        
      } else {
        button.classList.remove('is-active');
        const textSpan = button.querySelector('.wishlist-text');
        if(textSpan) textSpan.textContent = window.themeVariables.strings.addToWishlist;
      }
    });
  }

  animateButton(button) {
    button.classList.add('wishlist-bounce');
    setTimeout(() => {
      button.classList.remove('wishlist-bounce');
    }, 600);
  }

  showNotification(message, type = 'add') {
    // Remove existing notification
    const existingNotification = document.querySelector('.wishlist-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create new notification
    const notification = document.createElement('div');
    notification.className = `wishlist-notification ${type}`;
    notification.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 100 100">
        <path d="M88.5,16.3c-5.4-5.4-12.4-8.1-19.4-8.1s-14,2.7-19.4,8.1c-5.4-5.4-12.4-8.1-19.4-8.1s-14,2.7-19.4,8.1C0.3,27.1,0.3,44.3,11.1,55l38.7,38.7L88.5,55C99.8,44.3,99.8,27,88.5,16.3z"/>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => notification.classList.add('show'), 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
  checkEmptyState() {
    const wishlistGrid = document.querySelector('.wishlist-grid');
    const emptyState = document.querySelector('.wishlist-empty');

    if (wishlistGrid && emptyState) {
      const items = wishlistGrid.querySelectorAll('.wishlist-item');
      if (items.length === 0) {
        wishlistGrid.style.display = 'none';
        emptyState.style.display = 'flex';
      }
    }
  }

  dispatchUpdateEvent() {
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { count: this.getWishlist().length }
    }));
  }

  getCount() {
    return this.getWishlist().length;
  }
}

// Initialize wishlist manager
window.wishlistManager = new WishlistManager();

// Expose to window for template access
window.getWishlist = () => window.wishlistManager.getWishlist();
window.getWishlistCount = () => window.wishlistManager.getCount();